import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import nanoid from 'nanoid';
import moment from 'moment';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import range from 'lodash/range';
import clamp from 'lodash/clamp';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import upperFirst from 'lodash/upperFirst';

import { getUserFullName } from '../../utils/utilsChat';
import {
  exposeComponentToGlobalScope,
  isMobileSizeScreen,
  isWithinElement,
  withMomentLocale,
} from '../../utils/utilsGlobal';

import { detectTabFocus } from '../../decorators';
import {
  getWeekSchedule,
  getScheduleTimeOffPeriods,
  saveWeekSchedule,
  addScheduleTimeOff,
  removeScheduleTimeOff,
  getMaxOpenCases
} from '../../services/apiSchedule';

import {
  Modal,
  Datepicker,
  EditableSelect,
  LoadingSpinner,
  Tooltip,
  InputTime,
  showAlert,
} from '../../components';

import uiStyles from '../../styles/ui.scss';
import styles from './ScheduleSettingsEditPopup.scss';


const mapStateToProps = state => {
  return {
    currentCompany: state.currentCompany,
  };
};

@connect(mapStateToProps)
@detectTabFocus()
export class ScheduleSettingsEditPopup extends Component {

  state = {
    isLoading: true,
    activeTab: 'schedule',
    schedule: [],
    scheduleOriginal: [],
    isLargeBottomMargin: false,
    isEditingSchedule: false,
    isSavingSchedule: false,
    isAddingTimeOff: false,
    removableTimeOffPeriodId: null,
    editableDay: null,
    scheduleStartDate: null,
    timeOffDateFrom: null,
    timeOffMonthFrom: null,
    timeOffDateTo: null,
    timeOffMonthTo: null,
    maxOpenCases: 0
  };

  DEFAULT_HOURS_FROM = '10:00';
  DEFAULT_HOURS_TO = '19:00';
  DEFAULT_BREAK_FROM = '12:00';
  DEFAULT_BREAK_TO = '13:00';
  TIME_OFF_MONTH_ITEMS_LENGTH = 3;

  componentDidMount(){
    const defaultTimeOffFrom = moment();
    const defaultTimeOffTo = moment(defaultTimeOffFrom).add(3, 'days');

    this.setState({
      scheduleStartDate: new Date(),
      timeOffDateFrom: defaultTimeOffFrom.get('date'),
      timeOffMonthFrom: defaultTimeOffFrom.get('month'),
      timeOffDateTo: defaultTimeOffTo.get('date'),
      timeOffMonthTo: defaultTimeOffTo.get('month'),
    });



    Promise.all([
      this.fetchSchedule(),
      this.fetchTimeOffPeriods(),
      this.getActiveChatInfo()
    ]).then(([schedule, timeOffPeriods, maxOpenCases]) => {
      this.setState({
        timeOffPeriods,
        schedule,
        scheduleOriginal: schedule,
        isLoading: false,
        maxOpenCases
      });
    });
    exposeComponentToGlobalScope(this);

  }

  fetchSchedule = () => {
    const { currentCompany, operator } = this.props;
    return getWeekSchedule(currentCompany.id, operator.id);
  };

  fetchTimeOffPeriods = () => {
    const { currentCompany, operator } = this.props;
    return getScheduleTimeOffPeriods(currentCompany.id, operator.id);
  };

  startDayEditing = (day) => {
    this.setState({
      editableDay: this.normalizeEditableDay(day)
    });
    if (day.weekDay === 'SATURDAY' || day.weekDay === 'SUNDAY') {
      // Saturday or Sunday are in the bottom of the modal window
      // therefore they span all the way below the modal bottom bound
      this.enableLargeBottomMargin();
    }
  };

  enableLargeBottomMargin = () => {
    this.setState({ isLargeBottomMargin: true });
  };

  generateDayTypeOptions = () => {
    const { operator } = this.props;
    const name = getUserFullName(operator);
    return [
      {
        title: `${name} работает`,
        value: 'working',
      },
      {
        title: `${name} не работает`,
        value: 'timeOff',
      },
    ];
  };

  changeDayType = (type) => {
    const { editableDay, scheduleOriginal } = this.state;
    if (editableDay.type === type) {
      return;
    }
    const updatedEditableDay = { ...editableDay, type };

    if (type === 'working') {
      // Restoring previous hours & breaks
      if (!updatedEditableDay.beginTime || !updatedEditableDay.endTime) {
        const originalDay = find(scheduleOriginal, { id: editableDay.id });
        if (originalDay && originalDay.type === 'working') {
          updatedEditableDay.beginTime = originalDay.beginTime;
          updatedEditableDay.endTime = originalDay.endTime;
          updatedEditableDay.breaks = originalDay.breaks;
        }
        else {
          updatedEditableDay.beginTime = this.DEFAULT_HOURS_FROM;
          updatedEditableDay.endTime = this.DEFAULT_HOURS_TO;
        }
      }
    }
    this.setState({
      editableDay: this.normalizeEditableDay(updatedEditableDay),
    }, this.onScheduleStateManualUpdate);
  };

  changeDayHours = (type, value) => {
    const { editableDay } = this.state;
    const updatedEditableDay = {
      ...editableDay,
      [type]: value,
    };
    this.setState({
      editableDay: this.normalizeEditableDay(updatedEditableDay),
    }, this.onScheduleStateManualUpdate);
  };

  changeDayBreak = (id, type, value) => {
    const { editableDay } = this.state;
    const breakIndex = findIndex(editableDay.breaks, { id });
    const breaks = cloneDeep(editableDay.breaks);
    breaks[breakIndex][type] = value;
    const updatedEditableDay = { ...editableDay, breaks };
    this.setState({
      editableDay: this.normalizeEditableDay(updatedEditableDay)
    }, this.onScheduleStateManualUpdate);
  };

  removeDayBreak = (breakId) => {
    const { editableDay } = this.state;
    const breaks = editableDay.breaks.filter(breakItem => breakItem.id !== breakId);
    const updatedEditableDay = { ...editableDay, breaks };
    this.setState({
      editableDay: this.normalizeEditableDay(updatedEditableDay)
    }, this.onScheduleStateManualUpdate);
  };

  addDayBreak = () => {
    const { editableDay } = this.state;
    const breaks = editableDay.breaks.concat({
      id: nanoid(),
      beginTime: this.DEFAULT_BREAK_FROM,
      endTime: this.DEFAULT_BREAK_TO,
    });
    const updatedEditableDay = { ...editableDay, breaks };
    this.setState({
      editableDay: this.normalizeEditableDay(updatedEditableDay)
    }, this.onScheduleStateManualUpdate);
  };

  areDaysEqual = (day1, day2) => {
    const extractEssentialData = (day) => {
      const { type, beginTime, endTime, endNextDay, breaks } = day;
      return {
        type,
        beginTime,
        endTime,
        endNextDay: endNextDay || false,
        breaks: [...breaks].map(breakItem => {
          const { beginTime, endTime, startNextDay, endNextDay } = breakItem;
          return {
            beginTime,
            endTime,
            startNextDay: startNextDay || false,
            endNextDay: endNextDay || false,
          };
        }),
      };
    };
    return isEqual(
      extractEssentialData(day1),
      extractEssentialData(day2)
    );
  };

  onScheduleStateManualUpdate = () => {
    const { schedule, scheduleOriginal, editableDay } = this.state;
    const editedDayIndex = findIndex(schedule, { id: editableDay.id });

    let updatedSchedule = [ ...schedule ];
    updatedSchedule.splice(editedDayIndex, 1, editableDay);

    updatedSchedule = updatedSchedule.map(day => {
      const originalDay = find(scheduleOriginal, { weekDay: day.weekDay });
      return {
        ...day,
        isEdited: !this.areDaysEqual(day, originalDay),
      };
    });
    this.setState({
      schedule: updatedSchedule,
    });
  };

  normalizeEditableDay = (editableDay) => {
    const { id, type, weekDay, beginTime, endTime, breaks } = editableDay;
    const dayStartObject = moment(beginTime, 'HH:mm');
    const dayEndObject = moment(endTime, 'HH:mm');
    let dayEndNextDay = false;

    const doesDayEndTomorrow = moment(endTime, 'HH:mm').isSameOrBefore(dayStartObject, 'minute');
    if (doesDayEndTomorrow) {
      dayEndObject.add(1, 'days');
      dayEndNextDay = true;
    }

    const normalizedBreaks = breaks.map(breakItem => {
      const breakStartObject = moment(breakItem.beginTime, 'HH:mm');
      const breakEndObject = moment(breakItem.endTime, 'HH:mm');
      let breakStartNextDay = false;
      let breakEndNextDay = false;

      if (breakStartObject.isSameOrBefore(dayStartObject, 'minute')) {
        breakStartObject.add(1, 'days');
        breakStartNextDay = true;
      }
      if (breakEndObject.isSameOrBefore(breakStartObject, 'minute')) {
        breakEndObject.add(1, 'days');
        breakEndNextDay = true;
      }
      const isBeginTimeInvalid = !breakStartObject.isBetween(dayStartObject, dayEndObject, 'minute');
      const isEndTimeInvalid = !breakEndObject.isBetween(dayStartObject, dayEndObject, 'minute');
      return {
        id: breakItem.id,
        beginTime: breakItem.beginTime,
        endTime: breakItem.endTime,
        startNextDay: breakStartNextDay,
        endNextDay: breakEndNextDay,
        isBeginTimeInvalid,
        isEndTimeInvalid,
      };
    });

    return {
      id,
      type,
      weekDay,
      beginTime,
      endTime,
      endNextDay: dayEndNextDay,
      breaks: normalizedBreaks,
    };
  };

  formatScheduleForSaving = (schedule) => {
    return schedule
      .filter(item => item.type === 'working')
      .map(dayItem => {
        return {
          weekDay: dayItem.weekDay,
          beginTime: dayItem.beginTime + ':00',
          endTime: dayItem.endTime + ':00',
          endNextDay: dayItem.endNextDay,
          breaks: dayItem.breaks.map(breakItem => {
            return {
              beginTime: breakItem.beginTime + ':00',
              endTime: breakItem.endTime + ':00',
              startNextDay: breakItem.startNextDay,
              endNextDay: breakItem.endNextDay,
            };
          }),
        };
      });
  };

  /**
   * Получение списка активных чатов для оператора
   */
  getActiveChatInfo = () => {
    const { currentCompany, operator } = this.props;
    return getMaxOpenCases(currentCompany.id, operator.id)
  }

  saveSchedule = () => {
    const { currentCompany, operator, onChange } = this.props;
    const {
      schedule,
      scheduleStartDate, timeOffPeriods,
      maxOpenCases
    } = this.state;
    const requestData = {
      weekly: this.formatScheduleForSaving(schedule),
      startDate: moment(scheduleStartDate).format('YYYY-MM-DD'),
    };

    this.setState({ isSavingSchedule: true });
    saveWeekSchedule(currentCompany.id, operator.id, requestData, +maxOpenCases)
      .then(updatedSchedule => {
        this.setState({
          schedule: updatedSchedule,
          scheduleOriginal: updatedSchedule,
          isEditingSchedule: false,
          editableDay: null,
        });
        showAlert({ text: 'Расписание успешно сохранено' });
        onChange();
      })
      .catch(e => {
        const errorMessages = {
          'WORKDAYS_OVERLAP': 'Рабочие дни не должны пересекаться',
          'BREAKS_OVERLAP': 'Перерывы не должны пересекаться',
          'BREAKS_MISMATCH_WORKDAY': 'Перерывы должны быть в пределах рабочего времени',
        };
        if (errorMessages[e.errorType]) {
          e.overrideErrorNotification({ errorMessage: errorMessages[e.errorType] });
        }
      })
      .finally(() => {
        this.setState({ isSavingSchedule: false });
      });
  };

  onCancelEditingSchedule = () => {
    const { scheduleOriginal } = this.state;
    this.setState({
      schedule: scheduleOriginal,
      isEditingSchedule: false,
    });
  };

  switchTab = (activeTab) => {
    this.setState({
      editableDay: null,
      isLargeBottomMargin: false,
      isAddingTimeOff: false,
      removableTimeOffPeriodId: null,
      activeTab,
    });
  };

  onScheduleStartDateChange = (scheduleStartDate) => {
    this.setState({ scheduleStartDate });
  };

  onModalClick = (e) => {
    const target = e.target;

    if (!isWithinElement(target, styles.editDayPopup) && !isWithinElement(target, styles.scheduleData)) {
      this.setState({ editableDay: null });
    }
    if (!isWithinElement(target, styles.timeOffRemovePopup) && !isWithinElement(target, styles.timeOffItemData)) {
      this.setState({ removableTimeOffPeriodId: null });
    }
    if (!isWithinElement(target, styles.timeOffAddPopup) && !isWithinElement(target, styles.timeOffAddButton)) {
      this.setState({ isAddingTimeOff: false });
    }
  };

  generateTimeOffMonthOptions = () => {
    const months = [
      'января',
      'февраля',
      'марта',
      'апреля',
      'мая',
      'июня',
      'июля',
      'августа',
      'сентября',
      'октября',
      'ноября',
      'декабря',
    ];
    const currentMonthIndex = new Date().getMonth();
    const monthIndexes = range(currentMonthIndex, currentMonthIndex + this.TIME_OFF_MONTH_ITEMS_LENGTH);
    return monthIndexes.map(monthIndex => {
      monthIndex = monthIndex < 12 ? monthIndex : monthIndex - 12;
      return {
        title: months[monthIndex],
        value: monthIndex,
      };
    });
  };

  onTimeOffPeriodChange = (value, type) => {
    this.setState({
      ['timeOff' + type]: value,
    });
  };

  onTimeOffDateBlur = (value, type) => {
    const { timeOffMonthFrom, timeOffMonthTo } = this.state;
    const monthIndex = type === 'DateFrom' ? timeOffMonthFrom : timeOffMonthTo;
    const daysInMonth = moment().month(monthIndex).daysInMonth();
    this.setState({
      ['timeOff' + type]: clamp(+value, 1, daysInMonth),
    });
  };

  getDayName = (dayNameEn) => {
    const dayIndex = withMomentLocale('en', () => moment(dayNameEn, 'dddd').get('day'));
    return withMomentLocale('ru', () => {
      return upperFirst(moment().day(dayIndex).format('dddd'));
    });
  };

  addTimeOffPeriod = () => {
    const { currentCompany, operator, onChange } = this.props;
    const {
      timeOffPeriods,
      timeOffDateFrom,
      timeOffMonthFrom,
      timeOffDateTo,
      timeOffMonthTo,
    } = this.state;

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const startYear = timeOffMonthFrom >= currentMonth ? currentYear : currentYear + 1;
    const endYear = timeOffMonthTo >= currentMonth ? currentYear : currentYear + 1;
    const startDate = moment([startYear, timeOffMonthFrom, timeOffDateFrom]).format('YYYY-MM-DD');
    const endDate = moment([endYear, timeOffMonthTo, timeOffDateTo]).format('YYYY-MM-DD');
    this.setState({ isAddingTimeOff: false });

    addScheduleTimeOff(currentCompany.id, operator.id, startDate, endDate)
      .then((createdTimeOffPeriod) => {
        this.setState({
          timeOffPeriods: [
            ...timeOffPeriods,
            createdTimeOffPeriod,
          ]
        });
        onChange();
      })
      .catch(e => {
        const errorMessages = {
          'END_DATE_EARLIER_THAN_START_DATE': 'Дата начала периода позже, чем дата окончания',
          'START_DATE_MUST_BE_IN_FUTURE': 'Период вне работы нельзя создать в прошлом',
          'EXCEPTIONS_OVERLAP': 'Периоды вне работы не должны пересекаться',
        };
        if (errorMessages[e.errorType]) {
          e.overrideErrorNotification({ errorMessage: errorMessages[e.errorType] });
        }
      });
  };

  removeTimeOffPeriod = (periodId) => {
    const { currentCompany, onChange } = this.props;
    const { timeOffPeriods } = this.state;
    this.setState({
      timeOffPeriods: timeOffPeriods.filter(period => period.id !== periodId),
      removableTimeOffPeriodId: null,
    });
    removeScheduleTimeOff(currentCompany.id, periodId)
      .then(onChange)
      .catch(() => {
        this.setState({ timeOffPeriods });
      });
  };

  renderEditDayPopup = () => {
    const { editableDay } = this.state;
    return (
      <Fragment>
        <EditableSelect
          overrideClassNames={{
            container: styles.editDayType,
            value: styles.editDayTypeValue,
          }}
          defaultValue={editableDay.type}
          options={this.generateDayTypeOptions()}
          onChange={({ value }) => this.changeDayType(value)}/>

        {editableDay.type === 'working' && (
          <Fragment>
            <div className={styles.editDayTitle}>Рабочее время</div>
            <ul className={styles.editDayPeriodList}>
              <li className={styles.editDayPeriod}>
                <InputTime className={styles.editDayPeriodInput}
                  value={editableDay.beginTime}
                  onChange={value => this.changeDayHours('beginTime', value)}/>
                —
                <InputTime className={styles.editDayPeriodInput}
                  value={editableDay.endTime}
                  onChange={value => this.changeDayHours('endTime', value)}/>
              </li>
            </ul>

            <div className={styles.editDayTitle}>Перерывы</div>
            <ul className={styles.editDayPeriodList}>
              {editableDay.breaks.map((breakItem, i) => (
                <li key={i} className={styles.editDayPeriod}>
                  <InputTime
                    className={cn({
                      [styles.editDayPeriodInput]: true,
                      [styles.editDayPeriodInputInvalid]: breakItem.isBeginTimeInvalid,
                    })}
                    value={breakItem.beginTime}
                    onChange={value => this.changeDayBreak(breakItem.id, 'beginTime', value)}/>
                  —
                  <InputTime
                    className={cn({
                      [styles.editDayPeriodInput]: true,
                      [styles.editDayPeriodInputInvalid]: breakItem.isEndTimeInvalid,
                    })}
                    value={breakItem.endTime}
                    onChange={value => this.changeDayBreak(breakItem.id, 'endTime', value)}/>

                  <i className={cn(styles.editDayPeriodRemove, 'icon-close-thick')}
                    onClick={() => this.removeDayBreak(breakItem.id)}/>
                </li>
              ))}
            </ul>

            <span className={styles.editDayPeriodAdd} onClick={this.addDayBreak}>
              Добавить перерыв
            </span>
          </Fragment>
        )}
      </Fragment>
    );
  };

  onChangemaxOpenCases = (e) => {
    const newValue = e.target.value.replace(/[^0-9]/g, "");
    this.setState({maxOpenCases: newValue});
  }

  render(){
    const { className, operator, onClose } = this.props;
    const {
      isLoading,
      activeTab,
      schedule,
      scheduleStartDate,
      isEditingSchedule,
      isLargeBottomMargin,
      editableDay,
      timeOffPeriods,
      timeOffDateFrom,
      timeOffMonthFrom,
      timeOffDateTo,
      timeOffMonthTo,
      isAddingTimeOff,
      removableTimeOffPeriodId,
      isSavingSchedule,
      maxOpenCases
    } = this.state;

    const isMobileSize = isMobileSizeScreen();

    return (
      <Modal
        overrideClassNames={{
          container: className,
          modal: cn({
            [styles.modal]: true,
            [styles.modalExpandedHeight]: isEditingSchedule,
            [styles.modalBottomOffset]: isLargeBottomMargin,
          }),
        }}
        title={getUserFullName(operator)}
        onClick={this.onModalClick}
        onClose={onClose}>

        <div>
          <label>Активно чатов:</label>
          <input className={cn(styles.input)}
             disabled={!isEditingSchedule}
             maxLength={2}
             value={maxOpenCases}
             onChange={this.onChangemaxOpenCases}/>
        </div>

        <ul className={styles.tabs}>
          <li className={cn({
            [styles.tabsItem]: true,
            [styles.tabsItemActive]: activeTab === 'schedule',
          })} onClick={() => this.switchTab('schedule')}>
            {isMobileSize ? 'График работы' : 'Текущий график работы'}
          </li>
          <li className={cn({
            [styles.tabsItem]: true,
            [styles.tabsItemActive]: activeTab === 'timeOff',
          })} onClick={() => this.switchTab('timeOff')}>
            {isMobileSize ? 'Вне работы' : 'Периоды вне работы'}
          </li>
        </ul>

        {isLoading && (
          <div className={styles.loading}>
            <LoadingSpinner size="lg"/>
          </div>
        )}

        {!isLoading && activeTab === 'schedule' && (
          <Fragment>
            <div className={cn({
              [styles.scheduleEditingTitle]: true,
              [styles.scheduleEditingTitleExpanded]: isEditingSchedule,
            })}>
              Редактирование расписания
            </div>
            <ul className={cn({
              [styles.schedule]: true,
              [styles.scheduleEditing]: isEditingSchedule,
            })}>
              {schedule.map(day => (
                <li key={day.id} className={cn({
                  [styles.scheduleItem]: true,
                  [styles.scheduleItemInactive]: day.type === 'timeOff',
                })}>
                  <div className={styles.scheduleTitle}>
                    {this.getDayName(day.weekDay)}
                    {day.isEdited && (
                      <Tooltip title="Нажмите «Сохранить новое\n расписание», чтобы применить\n изменения">
                        <span className={styles.scheduleTitleEdited}>изменено</span>
                      </Tooltip>
                    )}
                  </div>
                  <span className={styles.scheduleData}
                    onClick={() => isEditingSchedule && this.startDayEditing(day)}>
                    {day.type === 'working' && (
                      <Fragment>
                        <span className={styles.scheduleHours}>{day.beginTime}–{day.endTime}</span>
                        {Boolean(day.breaks?.length) && (
                          'Перерывы ' +
                          day.breaks
                            .map(breakItem => `${breakItem.beginTime}–${breakItem.endTime}`)
                            .join(', ')
                        )}
                        {!Boolean(day.breaks?.length) && 'Без перерывов'}
                      </Fragment>
                    )}
                    {day.type === 'timeOff' && 'Не работает'}
                  </span>
                  <div className={cn({
                    [styles.editDayPopup]: true,
                    [styles.editDayPopupExpanded]: editableDay?.weekDay === day.weekDay,
                  })}>
                    {editableDay?.weekDay === day.weekDay && this.renderEditDayPopup()}
                  </div>
                </li>
              ))}
            </ul>

            <div className={cn({
              [styles.startDate]: true,
              [styles.startDateExpanded]: isEditingSchedule,
            })}>
              Новое расписание будет действовать {scheduleStartDate.getDate() === 2 ? 'со ' : 'с '}
              <Datepicker
                overrideClassNames={{
                  container: styles.startDatePicker,
                  calendar: styles.startDatePickerCalendar,
                }}
                onChange={this.onScheduleStartDateChange}
                onClick={this.enableLargeBottomMargin}
                defaultValue={new Date()}
                availableFrom={new Date()}>
                {withMomentLocale('ru', () => moment(scheduleStartDate).format('D MMMM'))}
                <i className={cn(styles.startDateArrow, 'icon-chevron-down')}/>
              </Datepicker>
            </div>

            {!isEditingSchedule && (
              <button className={uiStyles.buttonPrimary}
                onClick={() => this.setState({ isEditingSchedule: true })}>
                Внести изменения
              </button>
            )}

            {isEditingSchedule && (
              <Fragment>
                <button className={uiStyles.buttonPrimary}
                  onClick={this.saveSchedule}
                  disabled={isSavingSchedule}>
                  {isSavingSchedule && (isMobileSize ? 'Сохранение…' : 'Сохранение расписания…')}
                  {!isSavingSchedule && (isMobileSize ? 'Сохранить' : 'Сохранить новое расписание')}
                </button>
                <button className={cn(uiStyles.buttonSecondary, styles.cancelEditingSchedule)}
                  onClick={this.onCancelEditingSchedule}>
                  Отмена
                </button>
              </Fragment>
            )}
          </Fragment>
        )}

        {!isLoading && activeTab === 'timeOff' && (
          <div className={styles.timeOff}>
            <button className={cn(uiStyles.buttonSecondarySmall, styles.timeOffAddButton)}
              onClick={() => this.setState({ isAddingTimeOff: true })}>
              Добавить
            </button>

            <div className={cn({
              [styles.timeOffAddPopup]: true,
              [styles.timeOffAddPopupExpanded]: isAddingTimeOff,
            })}>
              <div className={styles.timeOffAddPopupTitle}>
                Менеджер не будет работать
              </div>
              <div className={styles.timeOffAddPopupDate}>
                <input className={styles.timeOffAddPopupInput}
                  onChange={(e) => this.onTimeOffPeriodChange(e.target.value, 'DateFrom')}
                  onBlur={(e) => this.onTimeOffDateBlur(e.target.value, 'DateFrom')}
                  value={timeOffDateFrom}
                  min={1}
                  max={31}
                  type="number"/>

                <EditableSelect
                  defaultValue={timeOffMonthFrom}
                  onChange={({ value }) => this.onTimeOffPeriodChange(value, 'MonthFrom')}
                  options={this.generateTimeOffMonthOptions()}/>

                <span className={styles.timeOffAddPopupDivider}>—</span>

                <input className={styles.timeOffAddPopupInput}
                  onChange={(e) => this.onTimeOffPeriodChange(e.target.value, 'DateTo')}
                  onBlur={(e) => this.onTimeOffDateBlur(e.target.value, 'DateTo')}
                  value={timeOffDateTo}
                  min={1}
                  max={31}
                  type="number"/>

                <EditableSelect
                  defaultValue={timeOffMonthTo}
                  onChange={({ value }) => this.onTimeOffPeriodChange(value, 'MonthTo')}
                  options={this.generateTimeOffMonthOptions()}/>
              </div>
              <button className={uiStyles.buttonPrimary} onClick={this.addTimeOffPeriod}>
                Добавить период
              </button>
            </div>

            <ul className={styles.timeOffList}>
              {timeOffPeriods.map(period => (
                <li className={styles.timeOffItem}
                  onClick={() => this.setState({ removableTimeOffPeriodId: period.id })}
                  key={period.id}>
                  <span className={styles.timeOffItemData}>
                    {withMomentLocale('ru', () => moment(period.startDate).format('D MMMM'))}
                    &nbsp;—&nbsp;
                    {withMomentLocale('ru', () => moment(period.endDate).format('D MMMM'))}
                    <i className={cn(styles.timeOffRemove, 'icon-close-thick')}/>
                  </span>
                  <div className={cn({
                    [styles.timeOffRemovePopup]: true,
                    [styles.timeOffRemovePopupExpanded]: removableTimeOffPeriodId === period.id,
                  })}>
                    <div className={styles.timeOffRemovePopupTitle}>
                      Удалить этот период вне работы?
                    </div>
                    <button className={uiStyles.buttonDanger} onClick={() => this.removeTimeOffPeriod(period.id)}>
                      Удалить
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    );
  }
}
